import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useDemoSession } from '../demo/DemoSession';
import { AnalyticsEvents, track } from '../services/AnalyticsService';
import { baselineOf, countDone, guideSteps, isTaskDone, type GuideBaseline } from './guideSteps';
import { useGuideSnapshot } from './useGuideSnapshot';
import Spotlight from './Spotlight';
import TourPanel from './TourPanel';
import { FinishOverlay, WelcomeOverlay } from './GuideOverlays';

type Phase = 'idle' | 'welcome' | 'tour' | 'finished';

interface GuideContextType {
  available: boolean;
  progress: { done: number; total: number } | null;
  start: () => void;
  exitDemo: () => void;
}

const GuideContext = createContext<GuideContextType | null>(null);

const NO_BASELINE: GuideBaseline = {};

export function GuideProvider({ children }: { children: React.ReactNode }) {
  const demo = useDemoSession();
  const { currentOrg, isBoardMember } = useOrganization();
  const navigate = useNavigate();
  // Anonymous demo visitors get the sign-up dialog whenever they try to leave; logged-in ones just leave.
  const promptSignUp = demo !== null && demo.viewer === null;
  const available = currentOrg !== null && (demo !== null || isBoardMember);
  const [phase, setPhase] = useState<Phase>(demo ? 'welcome' : 'idle');
  const snapshot = useGuideSnapshot(available ? currentOrg.id : null, phase === 'tour');
  // Demo tasks count only what the visitor adds on top of the seeded data; real tasks count everything.
  const [demoBaseline, setDemoBaseline] = useState<GuideBaseline | null>(null);
  const baseline = demo ? demoBaseline : NO_BASELINE;
  const [stepIndex, setStepIndex] = useState(0);
  const [leftEarly, setLeftEarly] = useState(false);

  useEffect(() => {
    if (demo && snapshot && !demoBaseline) setDemoBaseline(baselineOf(snapshot));
  }, [demo, snapshot, demoBaseline]);

  const progress = snapshot && baseline ? countDone(snapshot, baseline) : null;

  useEffect(() => {
    if (demo || !snapshot || !progress) return;
    const key = `ra_guide_prompted_${snapshot.org.id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    if (progress.done < progress.total) setPhase('welcome');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, snapshot?.org.id, progress !== null]);

  const goTo = (index: number) => {
    setStepIndex(index);
    if (currentOrg) navigate(`/org/${currentOrg.id}/${guideSteps[index].route}`);
  };

  const start = () => {
    const firstOpen = snapshot && baseline
      ? guideSteps.findIndex(step => step.tasks.some(task => !isTaskDone(task, snapshot, baseline)))
      : -1;
    setPhase('tour');
    goTo(demo || firstOpen < 0 ? 0 : firstOpen);
  };

  const finish = (early: boolean) => {
    track(AnalyticsEvents.DemoFinished, { tasksDone: progress?.done ?? 0, early });
    setLeftEarly(early);
    setPhase('finished');
  };

  const exitDemo = () => {
    if (promptSignUp) finish(true);
    else navigate(demo?.exitPath ?? '/');
  };

  const next = () => {
    if (demo) track(AnalyticsEvents.DemoStepCompleted, { step: guideSteps[stepIndex].id });
    if (stepIndex < guideSteps.length - 1) {
      goTo(stepIndex + 1);
    } else if (demo) {
      finish(false);
    } else {
      setPhase('idle');
    }
  };

  const step = guideSteps[stepIndex];
  const nextTask = snapshot && baseline ? step.tasks.find(task => !isTaskDone(task, snapshot, baseline)) : undefined;
  const orgName = currentOrg?.name ?? '';

  return (
    <GuideContext.Provider value={{ available, progress, start, exitDemo }}>
      {children}
      {available && phase === 'welcome' && (demo ? (
        <WelcomeOverlay
          badge="Testförening"
          title={`Välkommen till ${orgName}`}
          text={<>Du är styrelseledamot i <span className="font-semibold text-white">{orgName}</span>, en påhittad vägförening. Inget du gör här sparas, och inga SMS eller e-post skickas.</>}
          skipLabel="Utforska fritt"
          onStart={start}
          onSkip={() => setPhase('idle')}
        />
      ) : (
        <WelcomeOverlay
          badge="Kom igång"
          title={`Kom igång med ${orgName}`}
          text={<>{progress?.done ?? 0} av {progress?.total ?? 0} uppgifter är redan klara. Guiden visar vad som återstår för att föreningen ska vara redo.</>}
          skipLabel="Inte nu"
          onStart={start}
          onSkip={() => setPhase('idle')}
        />
      ))}
      {available && phase === 'tour' && (
        <>
          <Spotlight targets={nextTask?.targets ?? []} />
          <TourPanel
            stepIndex={stepIndex}
            snapshot={snapshot}
            baseline={baseline ?? NO_BASELINE}
            allDone={!demo && progress !== null && progress.done === progress.total}
            finishLabel={demo ? 'Slutför' : 'Stäng'}
            onNext={next}
            onPrev={() => goTo(stepIndex - 1)}
            onExit={promptSignUp ? () => finish(true) : () => setPhase('idle')}
          />
        </>
      )}
      {available && phase === 'finished' && demo && (
        <FinishOverlay
          title={leftEarly ? 'Redo att komma igång?' : 'Nu har du sett hela flödet'}
          tasksDone={progress?.done ?? 0}
          tasksTotal={progress?.total ?? 0}
          visitorLoggedIn={demo.viewer !== null}
          exitPath={demo.exitPath}
          onContinue={() => setPhase('idle')}
        />
      )}
    </GuideContext.Provider>
  );
}

export function useGuide() {
  const ctx = useContext(GuideContext);
  if (!ctx) throw new Error('useGuide must be used within GuideProvider');
  return ctx;
}

export function GuideNavButton({ onClick }: { onClick?: () => void }) {
  const { available, progress, start } = useGuide();
  if (!available) return null;
  const remaining = progress ? progress.total - progress.done : 0;

  return (
    <button
      onClick={() => { onClick?.(); start(); }}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all"
    >
      <Compass className="w-5 h-5 flex-shrink-0" />
      <span className="flex-1 text-left">Kom igång-guide</span>
      {progress && remaining > 0 && (
        <span className="bg-amber-100 text-amber-800 text-xs rounded-full px-2 py-0.5 font-medium">
          {progress.done}/{progress.total}
        </span>
      )}
    </button>
  );
}
